import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import DocumentAttachment from '../../../components/ui/DocumentAttachment';
import { getDocuments, saveDocuments } from '../../../utils/documentManager';
import { matchClientToProperties } from '../../../services/aiPropertyMatchingService';
import { propertyService } from '../../../services/propertyService';
import { useAuth } from '../../../contexts/AuthContext';
import { generateArabicPropertyRecommendations, analyzeClientPreferences } from '../../../services/aiArabicPropertyRecommendationService';

const ClientDetailsPanel = ({ client, onClose, onSendMessage, onScheduleFollowUp, onGenerateForm }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [clientDocuments, setClientDocuments] = useState([]);
  const [isMatchingProperties, setIsMatchingProperties] = useState(false);
  const [propertyMatches, setPropertyMatches] = useState(null);
  const { user } = useAuth();

  // NEW: States for AI-powered Arabic property recommendations
  const [aiRecommendations, setAiRecommendations] = useState(null);
  const [isGeneratingRecommendations, setIsGeneratingRecommendations] = useState(false);
  const [showAiRecommendations, setShowAiRecommendations] = useState(false);
  const [clientPreferencesAnalysis, setClientPreferencesAnalysis] = useState(null);
  const [isAnalyzingPreferences, setIsAnalyzingPreferences] = useState(false);

  React.useEffect(() => {
    if (client?.id) {
      const docs = getDocuments('client', client?.id);
      setClientDocuments(docs);
    }
  }, [client?.id]);

  const handleDocumentsChange = (docs) => {
    setClientDocuments(docs);
    if (client?.id) {
      saveDocuments('client', client?.id, docs);
    }
  };

  const handleMatchProperties = async () => {
    setIsMatchingProperties(true);
    try {
      const properties = await propertyService?.getAll(user?.id);
      const matches = await matchClientToProperties(client, properties);
      setPropertyMatches(matches);
      setActiveTab('matches');
    } catch (error) {
      alert(error?.message || 'فشل في مطابقة العقارات');
    } finally {
      setIsMatchingProperties(false);
    }
  };

  // NEW: Generate AI-powered Arabic property recommendations
  const handleGenerateAiRecommendations = async () => {
    setIsGeneratingRecommendations(true);
    try {
      const properties = await propertyService?.getAll(user?.id);
      
      if (!properties || properties?.length === 0) {
        alert('لا توجد عقارات متاحة للتوصية');
        return;
      }

      const recommendations = await generateArabicPropertyRecommendations(client, properties);
      setAiRecommendations(recommendations);
      setShowAiRecommendations(true);
      setActiveTab('ai-recommendations');
    } catch (error) {
      alert(error?.message || 'فشل في توليد التوصيات الذكية');
    } finally {
      setIsGeneratingRecommendations(false);
    }
  };

  // NEW: Analyze client preferences
  const handleAnalyzePreferences = async () => {
    setIsAnalyzingPreferences(true);
    try {
      const analysis = await analyzeClientPreferences(client);
      setClientPreferencesAnalysis(analysis);
    } catch (error) {
      alert(error?.message || 'فشل في تحليل التفضيلات');
    } finally {
      setIsAnalyzingPreferences(false);
    }
  };

  if (!client) {
    return (
      <div className="h-full flex items-center justify-center bg-card rounded-2xl border-2 border-border shadow-warm-lg" style={{
        borderColor: 'rgba(184, 134, 11, 0.3)',
        background: 'linear-gradient(135deg, #1A1A1A 0%, #242424 100%)'
      }}>
        <div className="text-center p-8">
          <Icon name="Users" size={48} color="#B8860B" className="mx-auto mb-4" />
          <p className="text-muted-foreground font-medium">اختر عميلاً لعرض التفاصيل</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-card rounded-2xl border-2 border-border shadow-warm-lg overflow-hidden" style={{
      borderColor: 'rgba(184, 134, 11, 0.3)',
      background: 'linear-gradient(135deg, #1A1A1A 0%, #242424 100%)'
    }}>
      <div className="p-4 border-b-2 border-border" style={{
        background: 'linear-gradient(135deg, rgba(184, 134, 11, 0.12) 0%, rgba(218, 165, 32, 0.12) 100%)'
      }}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-primary shadow-warm">
              <Image
                src={client?.avatar}
                alt={client?.avatarAlt}
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h3 className="text-lg font-heading font-bold text-foreground">
                {client?.name}
              </h3>
              <p className="text-sm text-muted-foreground font-caption">{client?.clientId}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted/50 rounded-lg transition-colors"
          >
            <Icon name="X" size={20} color="#B8860B" />
          </button>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-1">
          <button
            onClick={() => setActiveTab('overview')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap border-2 ${
              activeTab === 'overview' ?'bg-primary text-primary-foreground shadow-warm border-primary' :'bg-muted/30 text-muted-foreground hover:bg-muted/50 border-transparent'
            }`}
            style={{
              background: activeTab === 'overview' ?'linear-gradient(135deg, rgba(184, 134, 11, 0.9) 0%, rgba(218, 165, 32, 0.9) 100%)'
                : undefined
            }}
          >
            <Icon name="User" size={16} />
            نظرة عامة
          </button>
          <button
            onClick={() => setActiveTab('preferences')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap border-2 ${
              activeTab === 'preferences' ?'bg-primary text-primary-foreground shadow-warm border-primary' :'bg-muted/30 text-muted-foreground hover:bg-muted/50 border-transparent'
            }`}
            style={{
              background: activeTab === 'preferences' ?'linear-gradient(135deg, rgba(184, 134, 11, 0.9) 0%, rgba(218, 165, 32, 0.9) 100%)' : undefined
            }}
          >
            <Icon name="Settings" size={16} />
            التفضيلات
          </button>
          <button
            onClick={() => setActiveTab('matches')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap border-2 ${
              activeTab === 'matches' ?'bg-primary text-primary-foreground shadow-warm border-primary' :'bg-muted/30 text-muted-foreground hover:bg-muted/50 border-transparent'
            }`}
            style={{
              background: activeTab === 'matches' ?'linear-gradient(135deg, rgba(184, 134, 11, 0.9) 0%, rgba(218, 165, 32, 0.9) 100%)' : undefined
            }}
          >
            <Icon name="Home" size={16} />
            مطابقة العقارات
          </button>
          <button
            onClick={() => setActiveTab('ai-recommendations')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap border-2 ${
              activeTab === 'ai-recommendations' ?'bg-primary text-primary-foreground shadow-warm border-primary' :'bg-muted/30 text-muted-foreground hover:bg-muted/50 border-transparent'
            }`}
            style={{
              background: activeTab === 'ai-recommendations' ?'linear-gradient(135deg, rgba(184, 134, 11, 0.9) 0%, rgba(218, 165, 32, 0.9) 100%)' : undefined
            }}
          >
            <Icon name="Sparkles" size={16} />
            توصيات ذكية
          </button>
          <button
            onClick={() => setActiveTab('documents')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap border-2 ${
              activeTab === 'documents' ?'bg-primary text-primary-foreground shadow-warm border-primary' :'bg-muted/30 text-muted-foreground hover:bg-muted/50 border-transparent'
            }`}
            style={{
              background: activeTab === 'documents' ?'linear-gradient(135deg, rgba(184, 134, 11, 0.9) 0%, rgba(218, 165, 32, 0.9) 100%)' : undefined
            }}
          >
            <Icon name="FileText" size={16} />
            المستندات
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'overview' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-muted/30 rounded-lg p-3 border-2 border-border">
                <p className="text-xs text-muted-foreground mb-1">رقم الهاتف</p>
                <p className="text-sm font-medium text-foreground font-data">{client?.phone}</p>
              </div>
              <div className="bg-muted/30 rounded-lg p-3 border-2 border-border">
                <p className="text-xs text-muted-foreground mb-1">البريد الإلكتروني</p>
                <p className="text-sm font-medium text-foreground truncate">{client?.email}</p>
              </div>
              <div className="bg-muted/30 rounded-lg p-3 border-2 border-border">
                <p className="text-xs text-muted-foreground mb-1">نوع العميل</p>
                <p className="text-sm font-medium text-foreground">{client?.type}</p>
              </div>
              <div className="bg-muted/30 rounded-lg p-3 border-2 border-border">
                <p className="text-xs text-muted-foreground mb-1">الحالة</p>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  client?.status === 'active' ? 'bg-success/20 text-success' :
                  client?.status === 'pending'? 'bg-warning/20 text-warning' : 'bg-muted text-muted-foreground'
                }`}>
                  {client?.statusLabel}
                </span>
              </div>
            </div>

            {/* AI Preference Analysis Section */}
            <div className="bg-gradient-to-br from-primary/5 to-accent/5 rounded-lg p-4 border-2 border-primary/20">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Icon name="Brain" size={18} color="var(--color-primary)" />
                  <h4 className="text-sm font-semibold text-foreground">
                    تحليل التفضيلات بالذكاء الاصطناعي
                  </h4>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Sparkles"
                  iconPosition="right"
                  onClick={handleAnalyzePreferences}
                  disabled={isAnalyzingPreferences}
                >
                  {isAnalyzingPreferences ? 'جاري التحليل...' : 'تحليل الآن'}
                </Button>
              </div>

              {clientPreferencesAnalysis && (
                <div className="bg-card rounded-lg p-3 border border-border">
                  <h5 className="text-xs font-semibold text-foreground mb-2 flex items-center gap-1">
                    <Icon name="Lightbulb" size={14} />
                    اقتراحات لتحسين البحث:
                  </h5>
                  <ul className="space-y-2">
                    {clientPreferencesAnalysis?.suggestions?.map((suggestion, index) => (
                      <li key={index} className="flex items-start gap-2 text-xs text-foreground">
                        <Icon name="ArrowRight" size={12} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                        <span>{suggestion}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="bg-muted/30 rounded-lg p-4 border-2 border-border">
              <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="MessageSquare" size={16} />
                ملاحظات
              </h4>
              <p className="text-sm text-muted-foreground">
                {client?.notes || 'لا توجد ملاحظات'}
              </p>
            </div>
          </div>
        )}

        {activeTab === 'preferences' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3">
              <div className="flex items-center gap-3 p-4 rounded-xl border-2 border-border" style={{
                background: 'linear-gradient(135deg, rgba(184, 134, 11, 0.08) 0%, rgba(218, 165, 32, 0.08) 100%)'
              }}>
                <div className="p-2 rounded-lg bg-primary/10">
                  <Icon name="Phone" size={18} color="#B8860B" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground font-caption mb-1">رقم الهاتف</p>
                  <p className="text-sm font-data font-medium text-foreground">{client?.phone}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 rounded-xl border-2 border-border" style={{
                background: 'linear-gradient(135deg, rgba(184, 134, 11, 0.08) 0%, rgba(218, 165, 32, 0.08) 100%)'
              }}>
                <div className="p-2 rounded-lg bg-primary/10">
                  <Icon name="Mail" size={18} color="#B8860B" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground font-caption mb-1">البريد الإلكتروني</p>
                  <p className="text-sm font-medium text-foreground break-words">{client?.email}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 rounded-xl border-2 border-border" style={{
                background: 'linear-gradient(135deg, rgba(184, 134, 11, 0.08) 0%, rgba(218, 165, 32, 0.08) 100%)'
              }}>
                <div className="p-2 rounded-lg bg-primary/10">
                  <Icon name="MapPin" size={18} color="#B8860B" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground font-caption mb-1">الموقع المفضل</p>
                  <p className="text-sm font-medium text-foreground">{client?.preferredLocation}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 rounded-xl border-2 border-border" style={{
                background: 'linear-gradient(135deg, rgba(184, 134, 11, 0.08) 0%, rgba(218, 165, 32, 0.08) 100%)'
              }}>
                <div className="p-2 rounded-lg bg-primary/10">
                  <Icon name="DollarSign" size={18} color="#B8860B" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground font-caption mb-1">الميزانية</p>
                  <p className="text-sm font-data font-medium text-foreground">{client?.budget}</p>
                </div>
              </div>
            </div>

            <div className="p-4 rounded-xl border-2" style={{
              background: 'linear-gradient(135deg, rgba(184, 134, 11, 0.15) 0%, rgba(218, 165, 32, 0.15) 100%)',
              borderColor: 'rgba(184, 134, 11, 0.3)'
            }}>
              <h4 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
                <Icon name="Target" size={16} color="#B8860B" />
                الاهتمامات العقارية
              </h4>
              <div className="flex flex-wrap gap-3">
                {client?.interests?.map((interest, idx) => (
                  <span
                    key={idx}
                    className="px-4 py-2 text-sm font-medium rounded-full border-2 inline-block"
                    style={{
                      background: 'linear-gradient(135deg, rgba(184, 134, 11, 0.2) 0%, rgba(218, 165, 32, 0.2) 100%)',
                      borderColor: 'rgba(184, 134, 11, 0.4)',
                      color: '#DAA520'
                    }}
                  >
                    {interest}
                  </span>
                ))}
              </div>
            </div>

            {/* AI Property Matching Section */}
            <div className="p-4 rounded-xl border-2" style={{
              background: 'linear-gradient(135deg, rgba(184, 134, 11, 0.15) 0%, rgba(218, 165, 32, 0.15) 100%)',
              borderColor: 'rgba(184, 134, 11, 0.3)'
            }}>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-bold text-foreground flex items-center gap-2">
                  <Icon name="Sparkles" size={16} color="#B8860B" />
                  مطابقة ذكية للعقارات
                </h4>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleMatchProperties}
                  disabled={isMatchingProperties}
                  className="text-xs"
                >
                  {isMatchingProperties ? 'جاري المطابقة...' : 'مطابقة العقارات'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                استخدم الذكاء الاصطناعي لإيجاد أفضل العقارات المطابقة لتفضيلات العميل
              </p>
            </div>

            <div className="p-4 rounded-xl border-2 border-border" style={{
              background: 'linear-gradient(135deg, rgba(184, 134, 11, 0.08) 0%, rgba(218, 165, 32, 0.08) 100%)'
            }}>
              <h4 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
                <Icon name="FileText" size={16} color="#B8860B" />
                ملاحظات
              </h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {client?.notes}
              </p>
            </div>
          </div>
        )}

        {activeTab === 'matches' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-base font-semibold text-foreground flex items-center gap-2">
                <Icon name="Home" size={18} />
                مطابقة العقارات
              </h4>
              <Button
                variant="default"
                size="sm"
                iconName="Search"
                iconPosition="right"
                onClick={handleMatchProperties}
                disabled={isMatchingProperties}
              >
                {isMatchingProperties ? 'جاري البحث...' : 'بحت عن عقارات'}
              </Button>
            </div>

            {propertyMatches ? (
              <div className="space-y-3">
                {propertyMatches?.matches?.map((match, index) => (
                  <div key={index} className="bg-card rounded-lg p-4 border-2 border-border hover:border-primary/50 transition-all">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <h5 className="text-sm font-semibold text-foreground">{match?.property?.title}</h5>
                      <span className="px-2 py-1 rounded-full text-xs font-bold bg-primary/20 text-primary">
                        {match?.score}%
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">{match?.property?.location}</p>
                    <p className="text-xs text-foreground">{match?.reasoning}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Icon name="Search" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">
                  اضغط على "بحث عن عقارات" للعثور على عقارات مناسبة
                </p>
              </div>
            )}
          </div>
        )}

        {/* NEW: AI-Powered Arabic Recommendations Tab */}
        {activeTab === 'ai-recommendations' && (
          <div className="space-y-4">
            <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-lg p-4 border-2 border-primary/30 mb-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Icon name="Sparkles" size={20} color="var(--color-primary)" />
                  <h4 className="text-base font-semibold text-foreground">
                    توصيات عقارية ذكية باللغة العربية
                  </h4>
                </div>
                <Button
                  variant="default"
                  size="sm"
                  iconName="Zap"
                  iconPosition="right"
                  onClick={handleGenerateAiRecommendations}
                  disabled={isGeneratingRecommendations}
                >
                  {isGeneratingRecommendations ? 'جاري التوليد...' : 'توليد توصيات'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                استخدم GPT-5 لتوليد توصيات عقارية مخصصة بناءً على تفضيلات العميل وميزانيته
              </p>
            </div>

            {showAiRecommendations && aiRecommendations ? (
              <div className="space-y-4">
                <div className="bg-card rounded-lg p-3 border border-border">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Icon name="Home" size={12} />
                      إجمالي العقارات: {aiRecommendations?.totalProperties}
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="Star" size={12} />
                      التوصيات: {aiRecommendations?.recommendations?.length || 0}
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="Clock" size={12} />
                      {new Date(aiRecommendations?.generatedAt)?.toLocaleString('ar-SA')}
                    </span>
                  </div>
                </div>

                {aiRecommendations?.recommendations?.map((rec, index) => (
                  <div key={index} className="bg-card rounded-lg border-2 border-border hover:border-primary/50 transition-all overflow-hidden">
                    {/* Property Header */}
                    <div className="bg-gradient-to-r from-primary/10 to-accent/10 p-3 border-b border-border">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <h5 className="text-sm font-bold text-foreground mb-1">
                            {rec?.property?.title || `عقار ${index + 1}`}
                          </h5>
                          <p className="text-xs text-muted-foreground">
                            {rec?.property?.city} - {rec?.property?.neighborhood}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span className="px-3 py-1 rounded-full text-xs font-bold bg-primary text-primary-foreground">
                            {rec?.matchScore}%
                          </span>
                          <span className="text-xs font-bold text-primary">
                            {rec?.property?.price?.toLocaleString('ar-SA')} ريال
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Property Details */}
                    <div className="p-3 space-y-3">
                      {/* Reasoning */}
                      <div>
                        <h6 className="text-xs font-semibold text-foreground mb-1 flex items-center gap-1">
                          <Icon name="MessageSquare" size={12} />
                          سبب التوصية:
                        </h6>
                        <p className="text-xs text-foreground bg-accent/5 rounded p-2">
                          {rec?.reasoning}
                        </p>
                      </div>

                      {/* Strengths */}
                      {rec?.strengths?.length > 0 && (
                        <div>
                          <h6 className="text-xs font-semibold text-foreground mb-1 flex items-center gap-1">
                            <Icon name="CheckCircle" size={12} color="var(--color-success)" />
                            نقاط القوة:
                          </h6>
                          <ul className="space-y-1">
                            {rec?.strengths?.map((strength, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-xs text-foreground">
                                <Icon name="Plus" size={10} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                                <span>{strength}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Weaknesses */}
                      {rec?.weaknesses?.length > 0 && (
                        <div>
                          <h6 className="text-xs font-semibold text-foreground mb-1 flex items-center gap-1">
                            <Icon name="AlertCircle" size={12} color="var(--color-warning)" />
                            اعتبارات:
                          </h6>
                          <ul className="space-y-1">
                            {rec?.weaknesses?.map((weakness, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-xs text-muted-foreground">
                                <Icon name="Minus" size={10} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
                                <span>{weakness}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Advice */}
                      {rec?.advice && (
                        <div className="bg-primary/5 rounded p-2 border border-primary/20">
                          <h6 className="text-xs font-semibold text-foreground mb-1 flex items-center gap-1">
                            <Icon name="Lightbulb" size={12} color="var(--color-primary)" />
                            نصيحة:
                          </h6>
                          <p className="text-xs text-foreground">{rec?.advice}</p>
                        </div>
                      )}

                      {/* Property Specs */}
                      <div className="grid grid-cols-3 gap-2 pt-2 border-t border-border">
                        <div className="text-center">
                          <Icon name="Maximize" size={14} color="var(--color-muted-foreground)" className="mx-auto mb-1" />
                          <p className="text-xs text-muted-foreground">{rec?.property?.area} م²</p>
                        </div>
                        <div className="text-center">
                          <Icon name="Bed" size={14} color="var(--color-muted-foreground)" className="mx-auto mb-1" />
                          <p className="text-xs text-muted-foreground">{rec?.property?.bedrooms} غرف</p>
                        </div>
                        <div className="text-center">
                          <Icon name="Bath" size={14} color="var(--color-muted-foreground)" className="mx-auto mb-1" />
                          <p className="text-xs text-muted-foreground">{rec?.property?.bathrooms} حمام</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* AI Provider Info */}
                <div className="bg-gradient-to-r from-primary/5 to-accent/5 rounded-lg p-3 border border-primary/20">
                  <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                    <Icon name="Cpu" size={12} />
                    <span>مدعوم بواسطة OpenAI GPT-5 - تحليل ذكي باللغة العربية</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <Icon name="Sparkles" size={64} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                <p className="text-sm text-muted-foreground mb-2">
                  اضغط على "توليد توصيات" للحصول على توصيات عقارية ذكية
                </p>
                <p className="text-xs text-muted-foreground">
                  سيتم تحليل تفضيلات العميل ومطابقتها مع العقارات المتاحة
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="space-y-4">
            <div className="bg-muted/30 rounded-lg p-4 border border-border">
              <h3 className="text-base font-heading font-semibold text-foreground mb-2">
                مستندات العميل
              </h3>
              <p className="text-sm text-muted-foreground">
                جميع المستندات والملفات المتعلقة بهذا العميل
              </p>
            </div>
            <DocumentAttachment
              documents={clientDocuments}
              onChange={handleDocumentsChange}
              entityType="client"
              entityId={client?.id}
            />
          </div>
        )}
      </div>
      <div className="p-4 border-t border-border space-y-2">
        <Button
          variant="default"
          fullWidth
          iconName="MessageSquare"
          iconPosition="right"
          onClick={() => onSendMessage(client?.id)}
        >
          إرسال رسالة واتساب
        </Button>
        <div className="grid grid-cols-2 gap-2">
          <Button
            variant="outline"
            iconName="Calendar"
            iconPosition="right"
            onClick={() => onScheduleFollowUp(client?.id)}
          >
            جدولة متابعة
          </Button>
          <Button
            variant="outline"
            iconName="FileText"
            iconPosition="right"
            onClick={() => onGenerateForm(client?.id)}
          >
            إنشاء نموذج
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ClientDetailsPanel;